/**
 * ChatMarkdown — wraps MessageResponse (Streamdown) so Corpus labels in AI
 * replies render inline as CorpusChipPill, while normal markdown stays intact.
 *
 * Corpus matching happens before Streamdown sees the text. That keeps Corpus
 * chips out of Streamdown's inline-code/code-block renderer and gives us a
 * deterministic DOM marker for assistant chips.
 */
import { Children, useMemo, type ReactNode } from "react";
import { MessageResponse } from "@/components/ai-elements/message";
import { cn } from "@/lib/utils";
import { CorpusChipPill, type CorpusChipData, type CorpusChipKind } from "./CorpusChipPill";
import type { ChipResolver } from "./useCorpusChipResolver";
import { REF_MARKER_RE } from "@/lib/refMarkers";

const CORPUS_TAG = "corpus-chip";
const INLINE_CODE_CLASS =
  "rounded-sm border border-border/60 bg-muted/60 px-1 py-0.5 text-[0.9em] font-mono text-foreground";

const allowedTags = {
  [CORPUS_TAG]: ["data-kind", "data-id", "data-label"],
};

const childrenToPlainText = (children: ReactNode): string =>
  Children.toArray(children)
    .map((child) => (typeof child === "string" || typeof child === "number" ? String(child) : ""))
    .join("");

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const isWordChar = (value: string | undefined) => Boolean(value && /^[\p{L}\p{N}_]$/u.test(value));
const isTitleTokenChar = (value: string | undefined) => Boolean(value && /^[\p{L}\p{N}]$/u.test(value));

const titleTokens = (value: string) =>
  value
    .normalize("NFC")
    .replace(/[\u2018\u2019\u201B\u2032]/g, "'")
    .replace(/[\u201C\u201D\u201F\u2033]/g, '"')
    .replace(/[\u2013\u2014\u2212]/g, "-")
    .replace(/\s*&\s*/g, " and ")
    .replace(/[^\p{L}\p{N}\s]+/gu, " ")
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);

const advanceToNextTitleToken = (text: string, pos: number) => {
  let next = pos;
  while (next < text.length && !isTitleTokenChar(text[next]) && text[next] !== "&") next += 1;
  return next;
};

const readTitleTokenAt = (text: string, pos: number): { token: string; end: number } | null => {
  if (text[pos] === "&") return { token: "and", end: pos + 1 };
  if (!isTitleTokenChar(text[pos])) return null;
  let end = pos;
  while (end < text.length && isTitleTokenChar(text[end])) end += 1;
  return { token: text.slice(pos, end).normalize("NFC").toLowerCase(), end };
};

const matchTitleAt = (text: string, start: number, label: string): number | null => {
  if (!isTitleTokenChar(text[start])) return null;
  const expected = titleTokens(label);
  if (expected.length === 0) return null;

  let pos = start;
  for (let idx = 0; idx < expected.length; idx += 1) {
    if (idx > 0) pos = advanceToNextTitleToken(text, pos);
    const found = readTitleTokenAt(text, pos);
    if (!found || found.token !== expected[idx]) return null;
    pos = found.end;
  }
  return isWordChar(text[pos]) ? null : pos;
};

const corpusTag = (chip: CorpusChipData) =>
  `<${CORPUS_TAG} data-kind="${escapeHtml(chip.kind)}" data-id="${escapeHtml(chip.id)}" data-label="${escapeHtml(
    chip.label,
  )}">${escapeHtml(chip.label)}</${CORPUS_TAG}>`;

const countBacktickRun = (text: string, start: number) => {
  let end = start;
  while (text[end] === "`") end += 1;
  return end - start;
};

const findClosingBacktickRun = (text: string, start: number, runLength: number) => {
  const marker = "`".repeat(runLength);
  return text.indexOf(marker, start);
};

const REF_MARKER_ANCHORED = new RegExp("^" + REF_MARKER_RE.source);

function annotateCorpusChips(markdown: string, resolve: ChipResolver): string {
  const plainTextChips = resolve.plainTextChips ?? [];
  const hasRefMarker = markdown.includes("[ref:");
  if (!markdown || (plainTextChips.length === 0 && !markdown.includes("`") && !hasRefMarker)) return markdown;

  let out = "";
  let i = 0;

  const matchPlainChipAt = (start: number): { end: number; chip: CorpusChipData } | null => {
    const prev = start > 0 ? markdown[start - 1] : undefined;
    if (prev === "@" || isWordChar(prev)) return null;

    for (const chip of plainTextChips) {
      const end = matchTitleAt(markdown, start, chip.label.trim());
      if (end !== null) return { end, chip };
    }
    return null;
  };

  while (i < markdown.length) {
    if (markdown[i] === "`") {
      const runLength = countBacktickRun(markdown, i);
      const contentStart = i + runLength;
      const close = findClosingBacktickRun(markdown, contentStart, runLength);

      if (close === -1) {
        out += markdown[i];
        i += 1;
        continue;
      }

      if (runLength >= 3) {
        out += markdown.slice(i, close + runLength);
        i = close + runLength;
        continue;
      }

      const rawContent = markdown.slice(contentStart, close);
      const chip = resolve(rawContent.trim());
      if (chip) out += corpusTag(chip);
      else out += markdown.slice(i, close + runLength);
      i = close + runLength;
      continue;
    }

    if (markdown[i] === "[") {
      const rest = markdown.slice(i);
      const m = REF_MARKER_ANCHORED.exec(rest);
      if (m) {
        const kind = m[1];
        const id = m[2];
        const resolved = resolve.byRefMarker?.(kind, id) ?? null;
        if (resolved) {
          out += corpusTag(resolved);
        } else {
          const shortId = id.length > 8 ? `${id.slice(0, 8)}…` : id;
          out += corpusTag({ kind: "doc", id, label: `${kind}:${shortId}` });
        }
        i += m[0].length;
        continue;
      }
    }

    const matched = matchPlainChipAt(i);
    if (matched) {
      out += corpusTag(matched.chip);
      i = matched.end;
      continue;
    }

    out += markdown[i];
    i += 1;
  }

  return out;
}

type CorpusTagProps = Record<string, unknown> & { children?: ReactNode };

const CorpusTag = (props: CorpusTagProps) => {
  const label = String(props["data-label"] ?? childrenToPlainText(props.children) ?? "").trim();
  const kind = String(props["data-kind"] ?? "doc") as CorpusChipKind;

  if (!label) return null;
  return <CorpusChipPill kind={kind} label={label} surface="assistant" />;
};

function buildComponents() {
  const InlineCode = ({ children, className, ...rest }: React.HTMLAttributes<HTMLElement>) => (
    <code className={cn(INLINE_CODE_CLASS, className)} {...rest}>
      {children}
    </code>
  );

  return {
    inlineCode: InlineCode,
    [CORPUS_TAG]: CorpusTag,
  };
}

export const ChatMarkdown = ({
  children,
  resolveChip,
}: {
  children: string;
  resolveChip: ChipResolver;
}) => {
  const annotated = useMemo(() => annotateCorpusChips(children, resolveChip), [children, resolveChip]);
  const components = useMemo(() => buildComponents(), []);

  return (
    <MessageResponse allowedTags={allowedTags} components={components} literalTagContent={[CORPUS_TAG]}>
      {annotated}
    </MessageResponse>
  );
};

export default ChatMarkdown;